import {
  type OperationVariables,
  useQuery as useApolloQuery,
} from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { useCallback, useMemo } from 'react';

import { useIsFeatureFlagEnabled } from '../../feature-flags/useIsFeatureFlagEnabled';
import { useTRPC } from '../trpc';
import type { QueryHookConfig, QueryHookFactoryResult } from './types';

/**
 * Portal-local counterpart of web's `createQueryHook`. Both transports stay
 * mounted with one enabled at a time, and consumers receive the legacy
 * GraphQL result shape regardless of which transport served it.
 */
export const createQueryHook = <
  TQueryArgs,
  TTRPCOutput,
  TGraphQLData extends { __typename?: 'query_root' },
  TGraphQLVariables extends OperationVariables,
>(
  config: QueryHookConfig<
    TQueryArgs,
    TTRPCOutput,
    TGraphQLData,
    TGraphQLVariables
  >
): QueryHookFactoryResult<TQueryArgs, TGraphQLData> => {
  const {
    trpcQueryOptions,
    mapTrpcDataToGraphQL,
    graphqlDocument,
    graphqlVariables,
    graphqlFetchPolicy = 'no-cache',
    trpcStaleTime,
  } = config;

  return ({ queryArgs, shouldSkip, fetchPolicy }) => {
    const skip = shouldSkip ?? false;
    const trpcEnabled = useIsFeatureFlagEnabled('trpc');
    const trpc = useTRPC();

    const {
      data: trpcData,
      isPending: trpcLoading,
      refetch: trpcRefetch,
      error: trpcError,
    } = useQuery<
      TTRPCOutput,
      TRPCClientErrorLike<{ transformer: true; errorShape: unknown }>
    >({
      ...trpcQueryOptions(trpc, queryArgs),
      enabled: trpcEnabled && !skip,
      staleTime: trpcStaleTime,
    });

    const mappedTrpcData = useMemo(
      () =>
        trpcData === undefined ? undefined : mapTrpcDataToGraphQL(trpcData),
      [trpcData]
    );

    const trpcRefetchMapped = useCallback(async () => {
      const { data, error } = await trpcRefetch();

      return {
        data: data === undefined ? undefined : mapTrpcDataToGraphQL(data),
        error: error ?? null,
      };
    }, [trpcRefetch]);

    const graphqlVars = graphqlVariables?.(queryArgs);
    const {
      data: graphqlData,
      loading: graphqlLoading,
      refetch: graphqlRefetch,
      error: graphqlError,
    } = useApolloQuery(graphqlDocument, {
      variables: graphqlVars,
      fetchPolicy: fetchPolicy ?? graphqlFetchPolicy,
      skip: trpcEnabled || skip,
    });

    const graphqlRefetchMapped = useCallback(async () => {
      const { data, error } = await graphqlRefetch();

      return { data, error: error ?? null };
    }, [graphqlRefetch]);

    if (trpcEnabled) {
      return {
        loading: skip ? false : trpcLoading,
        data: mappedTrpcData,
        refetch: trpcRefetchMapped,
        error: trpcError,
      };
    }

    return {
      loading: graphqlLoading,
      data: graphqlData,
      refetch: graphqlRefetchMapped,
      error: graphqlError ?? null,
    };
  };
};
